import React, {useCallback, useEffect, useRef, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {isMobileOnly} from 'react-device-detect';
import debounce from 'lodash.debounce';
import {usePrevious} from 'helper';
import ModeSwitcher from './ModeSwitcher';
import {selectAccuracy, selectLat, selectLng, setFormattedAddress} from '../../features/liveLocation/liveLocationSlice';
import RecenterControl from '../../UI/RecenterControl';


const Map = props => {

    const googleMapRef = useRef();
    let prevLat;
    let prevLng;
    const [map, setMap] = useState(null);
    const [marker, setMarker] = useState(null);
    const [circle, setCircle] = useState(null);
    const dispatch = useDispatch();
    const lat = useSelector(selectLat);
    const lng = useSelector(selectLng);
    const accuracy = useSelector(selectAccuracy);


    const currentLocation = {lat: parseFloat(lat), lng: parseFloat(lng)};


    useEffect(() => {
        const googleMapScript = document.createElement('script');
        googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API}`;
        googleMapScript.async = true;

        window.document.body.appendChild(googleMapScript);

        googleMapScript.addEventListener("load", () => {
            initMap();
        });
    }, []);


    prevLat = usePrevious(lat);
    prevLng = usePrevious(lng);

    const result = [lat, lng];
    const position = [prevLat, prevLng];

    const NUM_DELTAS = 100;
    const DELAY = 10; //milliseconds
    let i = 0;
    let deltaLat;
    let deltaLng;


    const markerTransition = () => {

        if (lat && prevLat && lng && prevLng) {
            deltaLat = (result[0] - position[0]) / NUM_DELTAS;
            deltaLng = (result[1] - position[1]) / NUM_DELTAS;

            if (deltaLat !== 0 && deltaLng !== 0) {
                moveMarker();
            }
        }

    };

    const moveMarker = () => {

        position[0] += deltaLat;
        position[1] += deltaLng;

        const latlng = new window.google.maps.LatLng(position[0], position[1]);

        marker.setPosition(latlng);
        circle.setCenter(latlng);

        if (i !== NUM_DELTAS) {
            i++;
            setTimeout(moveMarker, DELAY);
        }
    }


    useEffect(() => {
        if (marker) {
            markerTransition(markerTransition);
        }
    }, [marker, lat, lng]);

    const createGoogleMap = (coordinates) => {
        const modePosition = isMobileOnly ? window.google.maps.ControlPosition.TOP_CENTER : window.google.maps.ControlPosition.LEFT_BOTTOM;
        const zoomPosition = isMobileOnly ? window.google.maps.ControlPosition.RIGHT_CENTER : window.google.maps.ControlPosition.RIGHT_BOTTOM;

        const googleMap = new window.google.maps.Map(googleMapRef.current, {
            zoom: 17,
            center: coordinates,
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            mapTypeId: 'roadmap',
            zoomControlOptions: {
                position: zoomPosition
            },
        });

        setMap(googleMap);

        const marker = new window.google.maps.Marker({
            position: coordinates,
            map: googleMap,
            draggable: false,
            animation: window.google.maps.Animation.DROP,
            icon: {
                url: './images/default-avatar.png',
                scaledSize: new window.google.maps.Size(100, 110),
            },
        });

        setMarker(marker);

        const circle = new window.google.maps.Circle({
            center: coordinates,
            radius: accuracy,
            fillColor: '#00BFA5',
            fillOpacity: '0.3',
            visible: true,
            map: googleMap,
            strokeColor: 'transparent'
        });

        setCircle(circle);
    };


    const initMap = () => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({location: {lat, lng}}, (results, status) => {
            if (status === 'OK') {
                if (results[0]) {
                    createGoogleMap(currentLocation);
                }
            }
        });
    }


    useEffect(() => {
        delayedRequest();
        return delayedRequest.cancel;
    }, [lat, lng]);


    const getGeoCodedAddress = () => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({location: {lat, lng}}, (results, status) => {
            if (status === 'OK') {
                if (results[0]) {
                    dispatch(setFormattedAddress(results[0].formatted_address));
                } else {
                    console.log('No results found');
                }
            } else {
                console.log('Geocoder failed due to: ' + status);
            }
        });
    };

    const delayedRequest = useCallback(debounce(getGeoCodedAddress, 1000), [lat, lng]);

    const onRecenterClick = () => map.panTo(currentLocation);


    return (
        <>
            <div
                ref={googleMapRef}
                id="google-map"
                style={{width: '100%', height: '100%'}}/>


            <ModeSwitcher map={map}/>
            <RecenterControl onClick={onRecenterClick}/>

        </>

    )
};

export default Map;